import { useSelector } from 'react-redux';
import { useTheme } from 'styled-components';
import { FilterIcon, XCloseIcon } from '@rsos/assets/icons';
import { Box, Dropdown, Text } from '@rsos/base-components';
import {
  DropdownBody,
  DropdownHeader,
} from '@rsos/base-components/Dropdown/DropdownComponents';
import { useCallQueueFilters } from '@rsos/reduxDataStore/src/features/selectors/callQueueSelector';
import useCallTakerModePerms from '../../../../hooks/useCallTakerModePerms';
import useHasAccessToTranscriptAndAudioPlayback from '../../../../hooks/useHasAccessToTranscriptAndAudioPlayback';
import { ActionBarItem } from '../ActionBarItem';
import {
  FilterCheckbox,
  CallQueueFilterDropdownDialog,
} from './CallQueueFilters.styles';

function CallQueueFilters() {
  const hasCallTakerModeAccess = useCallTakerModePerms();
  const hasTranscriptionAndAudioPlaybackAccess =
    useHasAccessToTranscriptAndAudioPlayback();
  const { callFilters, toggleSetCallFilter } = useCallQueueFilters();
  const theme = useTheme();
  const isCollapsed = useSelector(state => state.queue?.isQueueCollapsed);

  return (
    <Dropdown triggerElement={CallQueueFiltersActionBarItem} triggerOnClose>
      {onDropdownClose => (
        <CallQueueFilterDropdownDialog
          width="180px"
          zIndex={2}
          isCollapsed={isCollapsed}
          data-name="call-queue-filter-dropdown"
        >
          <DropdownHeader>
            <Text fontWeight="semibold" size="large" color="primaryText">
              Filter Queue by:
            </Text>
            <XCloseIcon
              data-name="close-button"
              onClick={() => onDropdownClose()}
              color={theme.colors.secondaryText}
            />
          </DropdownHeader>
          <DropdownBody>
            <Box paddingTop="11px" paddingBottom="8px" flow={8}>
              {hasTranscriptionAndAudioPlaybackAccess && (
                <Box>
                  <Text
                    fontWeight="semibold"
                    size="normal"
                    color="primaryText"
                    padding="8px 16px"
                  >
                    Incident Type
                  </Text>
                  <FilterCheckbox
                    label="Active Calls"
                    data-name="active-call-filter"
                    isChecked={callFilters.activeCalls}
                    onChange={() => toggleSetCallFilter('activeCalls')}
                  />
                  <FilterCheckbox
                    label="Admin Line Calls"
                    data-name="admin-call-filter"
                    isChecked={callFilters.adminCalls}
                    onChange={() => toggleSetCallFilter('adminCalls')}
                  />
                  <FilterCheckbox
                    label="911 Incidents"
                    data-name="911-call-filter"
                    isChecked={callFilters.incidentCalls}
                    onChange={() => toggleSetCallFilter('incidentCalls')}
                  />
                </Box>
              )}
              {hasCallTakerModeAccess && (
                <Box>
                  <Text
                    fontWeight="semibold"
                    size="normal"
                    color="primaryText"
                    padding="8px 16px"
                  >
                    Call Taker Mode
                  </Text>
                  <FilterCheckbox
                    label="Seat Position"
                    data-name="seat-position-filter"
                    isChecked={callFilters.seatPosition}
                    onChange={() => toggleSetCallFilter('seatPosition')}
                  />
                </Box>
              )}
            </Box>
          </DropdownBody>
        </CallQueueFilterDropdownDialog>
      )}
    </Dropdown>
  );
}

function CallQueueFiltersActionBarItem({ selected, onClick }) {
  const hasCallTakerModeAccess = useCallTakerModePerms();
  const { hasActiveCallFilters } = useCallQueueFilters();

  return (
    <ActionBarItem
      shouldRender={hasCallTakerModeAccess}
      data-name="call-queue-filter-action-item"
      tooltipText="Filter Call Queue"
      isActive={selected}
      onClick={onClick}
      iconDataName="call-queue-filter-action-bar-item-icon"
    >
      <FilterIcon color={hasActiveCallFilters ? 'success' : 'secondaryLink'} />
    </ActionBarItem>
  );
}

export default CallQueueFilters;
